import {
  FieldLabel,
  FormDescription,
  FormHeading,
  ScreenTooltip,
  useGoBack,
  useUiStore,
} from "@vixi/ui";
import { useTranslation } from "@vixi/localization";
import { Avatar } from "@vixi/types";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { Button, ButtonLink, Img, StyledTextLink } from "@vixi/ui";
import { useSaveAvatar } from "@/hooks/api/avatars/useSaveAvatar";
import { useNavigate } from "@vixi/router";
import { useQuery } from "@tanstack/react-query";
import { paymentInfoQueryOptions } from "@vixi/api";
import { allAvatarsCountQueryOptions } from "@/queries/avatars/allAvatarsCountQuery";
import { shouldShowAvatarUpgradePopup } from "@/utils/planCheck";
import ChevronRightCircle from "@assets/icons/chevron-right-circle.svg?react";

interface ConfirmCharacterStepProps {
  generatedAvatar: Avatar;
}

export const ConfirmNewCharacterStep: FC<ConfirmCharacterStepProps> = ({ generatedAvatar }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const setHeaderChildren = useUiStore((state) => state.header.setChildren);
  const showConfirmDialog = useUiStore((state) => state.showConfirmDialog);

  const [likesAvatar, setLikesAvatar] = useState<boolean | null>(null);
  const { mutateAsync: saveAvatar, isPending: isSavingAvatar } = useSaveAvatar();

  // Fetch payment info and avatars count for upgrade popup check
  const { data: paymentInfo, isSuccess: isPaymentInfoSuccess } = useQuery(paymentInfoQueryOptions());
  const { data: allAvatarsCount, isSuccess: isAvatarsCountSuccess } = useQuery(allAvatarsCountQueryOptions());

  // Debug: Log data when loaded
  useEffect(() => {
    if (isPaymentInfoSuccess) {
      console.log("✅ [ConfirmNewCharacterStep] PaymentInfo loaded:", paymentInfo);
    }
    if (isAvatarsCountSuccess) {
      console.log("✅ [ConfirmNewCharacterStep] AllAvatarsCount loaded:", allAvatarsCount);
    }
  }, [isPaymentInfoSuccess, isAvatarsCountSuccess, paymentInfo, allAvatarsCount]);

  // Memoize the upgrade popup content to prevent infinite re-renders
  const upgradePopupContent = useMemo(
    () => (
      <div className="relative w-full h-full flex flex-col items-center justify-center">
        {/* Background Image */}
        <Img
          src="/images/avatarpopp.png"
          alt="Vixi Premium"
          className="absolute top-0 left-0 inset-0 w-full h-full object-cover"
        />

        {/* Content Overlay - Centered */}
        <div className="relative  z-10 flex flex-col items-center justify-center gap-3">
          {/* Text */}
          <h1 className="text-[35px] font-bold text-center text-[#334D6E]">
            Generate and publish more courses !
          </h1>

          {/* Try Vixi Premium Button */}
          <Button
            variant="contained"
            color="premium"
            size="large"
            className="px-8 py-3 rounded-full bg-gradient-to-r from-purple-400 to-purple-600 hover:from-purple-500 hover:to-purple-700 text-white font-medium"
            startIcon={<ChevronRightCircle className="w-5 h-5" />}
            onClick={() => {
              useUiStore.getState().hideConfirmDialog();
              navigate({ to: "/premium/plans" });
            }}
          >
            {"Try Vixi Premium"}
          </Button>
        </div>
      </div>
    ),
    [navigate],
  );

  useEffect(() => {
    if (likesAvatar) {
      setHeaderChildren(null);
      return;
    }

    setHeaderChildren(<></>);

    return () => {
      setHeaderChildren(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [likesAvatar]);

  const back = useMemo(() => {
    if (likesAvatar || likesAvatar === null) return null;

    return () => {
      setLikesAvatar(null);
    };
  }, [likesAvatar]);
  useGoBack(back, { allowChanges: true });

  if (likesAvatar)
    return (
      <>
        <div className="fs-25 m-auto max-w-156.25 text-center font-bold">
          <h2 className="fs-inherit fw-inherit text-success">{t("SuccessMessage.Awesome")}</h2>
          <p>{t("CreateAvatar.NewAvatarAdded")}</p>
          <ButtonLink to="/avatars" variant="contained" size="medium" className="mt-4">
            {t("Avatars.MyAvatars")}
          </ButtonLink>
        </div>
      </>
    );

  return (
    <div>
      <FormHeading heading="CreateAvatar.YourNewCharacter" showDivider={false}>
        <FormDescription className="mt-2.5">
          <span className="flex items-end gap-3">
            {t("CreateAvatar.YourNewCharacterDescription")}.
            <ScreenTooltip image="/images/screens/avatar-in-course.png">
              <StyledTextLink>{t("Action.SeeHow")}</StyledTextLink>
            </ScreenTooltip>
          </span>
        </FormDescription>
      </FormHeading>

      <div className="mt-15.5 grid grid-cols-2 gap-4">
        <div className="col-span-full lg:col-span-1">
          <div className="mx-auto aspect-31/55 h-100 max-h-[60vh] w-auto rounded p-5">
            {generatedAvatar && (
              <Img contain className="bg-paper h-full w-full rounded" src={generatedAvatar.url} />
            )}
          </div>
        </div>

        <div className="col-span-full lg:col-span-1">
          <div className="m-auto flex flex-col items-center">
            {likesAvatar === null ? (
              <>
                <FieldLabel>{t("CreateAvatar.DoYouLikeIt")}</FieldLabel>
                <div className="mt-2.5 flex gap-4.25">
                  <Button
                    size="pill"
                    variant="outlined"
                    className="w-26.25"
                    onClick={() => setLikesAvatar(false)}
                    disabled={isSavingAvatar}
                  >
                    {t("No")}
                  </Button>
                  <Button
                    size="pill"
                    variant="contained"
                    className="w-26.25"
                    onClick={async () => {
                      console.log("🔍 [ConfirmNewCharacterStep] Yes button clicked:", {
                        paymentInfo,
                        allAvatarsCount,
                        isPaymentInfoSuccess,
                        isAvatarsCountSuccess,
                        typeOfAllAvatarsCount: typeof allAvatarsCount,
                      });

                      // Check if user should see upgrade popup
                      // Only check if data is loaded
                      if (isPaymentInfoSuccess && isAvatarsCountSuccess) {
                        const shouldShow = shouldShowAvatarUpgradePopup(paymentInfo, allAvatarsCount);
                        console.log("🔍 [ConfirmNewCharacterStep] Checking avatar upgrade popup:", {
                          paymentInfo,
                          allAvatarsCount,
                          shouldShow,
                        });

                        if (shouldShow) {
                          console.log("✅ [ConfirmNewCharacterStep] Showing upgrade popup");
                          // Show upgrade popup instead of saving
                          showConfirmDialog({
                            title: null,
                            description: upgradePopupContent,
                            dialogActions: null,
                          });
                          return; // Don't save avatar
                        }
                      } else {
                        console.log("⚠️ [ConfirmNewCharacterStep] Data not loaded yet, proceeding with save");
                      }

                      // Save avatar normally
                      console.log("💾 [ConfirmNewCharacterStep] Saving avatar");
                      await saveAvatar(generatedAvatar);
                      setLikesAvatar(true);
                    }}
                    disabled={isSavingAvatar}
                  >
                    {t("Yes")}
                  </Button>
                </div>
              </>
            ) : (
              <>
                <FieldLabel>{t("CreateAvatar.GenerateNewAvatarNow")}</FieldLabel>
                <div className="mt-2.5 flex gap-4.25">
                  <Button
                    size="pill"
                    variant="outlined"
                    className="w-26.25"
                    onClick={() => navigate({ to: "/avatars" })}
                  >
                    {t("Later")}
                  </Button>

                  <Button
                    size="pill"
                    variant="contained"
                    className="w-26.25"
                    onClick={() => {
                      window.location.reload();
                    }}
                  >
                    {t("Yes")}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
